import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useInteractions,
  FloatingPortal,
  Placement,
} from '@floating-ui/react';

export interface DropdownProps {
  renderTitle?: React.ReactNode | ((props: { ref: React.Ref<HTMLElement>; className: string }) => React.ReactNode);
  title?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  menuClassName?: string;
  placement?: Placement;
  disabled?: boolean;
  trigger?: 'click' | 'hover';
  onOpen?: () => void;
  onClose?: () => void;
}

const Dropdown: React.FC<DropdownProps> = ({
  renderTitle,
  title,
  children,
  className,
  menuClassName,
  placement = 'bottom-start',
  disabled = false,
  trigger = 'click',
  onOpen,
  onClose,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      if (disabled) return;
      setIsOpen(open);
      if (open) onOpen?.();
      else onClose?.();
    },
    placement,
    middleware: [offset(8), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context, { enabled: trigger === 'click' });
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  const handleMouseEnter = useCallback(() => {
    if (trigger === 'hover' && !disabled) {
      setIsOpen(true);
      onOpen?.();
    }
  }, [trigger, disabled, onOpen]);

  const handleMouseLeave = useCallback(() => {
    if (trigger === 'hover') {
      setIsOpen(false);
      onClose?.();
    }
  }, [trigger, onClose]);

  const renderToggle = () => {
    if (typeof renderTitle === 'function') {
      return renderTitle({
        ref: refs.setReference as React.Ref<HTMLElement>,
        className: classNames('ecme-dropdown-toggle', className, { disabled }),
      });
    }

    // A custom node title supplies its own affordance (icon/pill) — don't
    // append the built-in chevron, it doubles up and can wrap below fixed-
    // height containers like the header bar.
    if (renderTitle) {
      return (
        <button
          ref={refs.setReference}
          type="button"
          className={classNames('ecme-dropdown-toggle', className, { disabled })}
          disabled={disabled}
          {...getReferenceProps()}
        >
          {renderTitle}
        </button>
      );
    }

    return (
      <button
        ref={refs.setReference}
        type="button"
        className={classNames('ecme-dropdown-toggle', className, { disabled })}
        disabled={disabled}
        {...getReferenceProps()}
      >
        {title}
        <svg
          className={classNames('w-4 h-4 ms-1 transition-transform', { 'rotate-180': isOpen })}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    );
  };

  return (
    <div
      className="ecme-dropdown"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {renderToggle()}
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={classNames('ecme-dropdown-menu', menuClassName)}
            {...getFloatingProps()}
          >
            {children}
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export interface DropdownItemProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  active?: boolean;
  disabled?: boolean;
  eventKey?: string;
  onSelect?: (eventKey: string) => void;
}

export const DropdownItem: React.FC<DropdownItemProps> = ({
  children,
  className,
  active = false,
  disabled = false,
  eventKey = '',
  onSelect,
  onClick,
  ...rest
}) => {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    onClick?.(e);
    onSelect?.(eventKey);
  };

  return (
    <div
      className={classNames('ecme-dropdown-item', className, { active, disabled })}
      onClick={handleClick}
      role="menuitem"
      {...rest}
    >
      {children}
    </div>
  );
};

export default Dropdown;
